import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'WPSL TV';
const DEFAULT_DESCRIPTION = "The streaming home of the Women's Premier Soccer League. Watch live matches, get scores, and follow your favorite teams across the United States.";
const DEFAULT_IMAGE = '/og-image.png';

/**
 * SEO Component for dynamic meta tags
 */
export const SEO = ({ 
  title, 
  description = DEFAULT_DESCRIPTION, 
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  article = null,
  noIndex = false
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Live Women's Soccer Streaming`;
  const baseUrl = window.location.origin;
  const fullImage = image?.startsWith('http') ? image : `${baseUrl}${image}`;
  const canonicalUrl = url ? (url.startsWith('http') ? url : `${baseUrl}${url}`) : window.location.href;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:url" content={canonicalUrl} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />
      
      {/* Article Specific Tags */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          {article.modifiedTime && (
            <meta property="article:modified_time" content={article.modifiedTime} />
          )}
          {article.author && (
            <meta property="article:author" content={article.author} />
          )}
          {article.tags?.map((tag, index) => (
            <meta property="article:tag" content={tag} key={index} />
          ))}
        </>
      )}
    </Helmet>
  );
};

/**
 * Generate structured data for an article
 */
export const ArticleStructuredData = ({ article }) => {
  if (!article) return null;
  
  const baseUrl = window.location.origin;
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "headline": article.title,
    "description": article.excerpt || article.content?.substring(0, 160),
    "image": article.featured_image?.startsWith('http') 
      ? article.featured_image 
      : `${baseUrl}${article.featured_image}`,
    "datePublished": article.published_at || article.created_at,
    "dateModified": article.updated_at || article.published_at || article.created_at,
    "author": {
      "@type": "Organization",
      "name": SITE_NAME
    },
    "publisher": {
      "@type": "Organization",
      "name": SITE_NAME,
      "logo": {
        "@type": "ImageObject",
        "url": `${baseUrl}/wpsl-logo.svg`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${baseUrl}/news/${article.slug}`
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

/**
 * Generate structured data for a sports event
 */
export const GameStructuredData = ({ game }) => {
  if (!game) return null;
  
  const baseUrl = window.location.origin;
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SportsEvent",
    "name": `${game.home_team_name} vs ${game.away_team_name}`,
    "startDate": game.scheduled_time,
    "location": {
      "@type": "Place",
      "name": game.location || "TBD"
    },
    "homeTeam": {
      "@type": "SportsTeam",
      "name": game.home_team_name
    },
    "awayTeam": {
      "@type": "SportsTeam",
      "name": game.away_team_name
    },
    "organizer": {
      "@type": "Organization",
      "name": game.league_name || SITE_NAME
    },
    "image": game.thumbnail || game.background_image_url,
    "url": `${baseUrl}/game/${game.id}`
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export default SEO;
